import {
  DeckStatus,
  ReportAvailability,
  ReportReadinessStepStatus,
  ReportSectionFailureReason,
  ReportSectionState,
  PublicStoryline,
} from '@digital-twins/shared-types/reporting'

import type { BoundaryParseResult } from '@/lib/validation/schema-loader'

/** An exhibit (one deck slide) the accepted publication marks failed. */
export interface FailedExhibit {
  slideId: string
  failureReason: ReportSectionFailureReason | null
  attemptId: string | null
}

/** What the page knows about the deck artifact itself. */
export interface ReportDeckInput {
  /** The deck status the study serves (or a live one from the socket). */
  status: DeckStatus | null | undefined
  /** A deck with at least one slide is loaded on the page. */
  hasSlides: boolean
  /** The deck artifact is being fetched. */
  isFetching: boolean
}

interface ReportViewStateBase {
  /** Exhibits that failed in the accepted publication, in publication order. */
  failedExhibits: Array<FailedExhibit>
}

/** A readable deck; `isUpdating` while a newer deck generates behind it. */
export interface DeckReadyViewState extends ReportViewStateBase {
  kind: 'deck_ready'
  isUpdating: boolean
}

export interface DeckFetchingViewState extends ReportViewStateBase {
  kind: 'deck_fetching'
}

/** No readable deck yet, one is being built. */
export interface DeckGeneratingViewState extends ReportViewStateBase {
  kind: 'deck_generating'
}

export interface DeckFailedViewState extends ReportViewStateBase {
  kind: 'deck_failed'
}

/** The study does not build a deck. */
export interface DeckSkippedViewState extends ReportViewStateBase {
  kind: 'deck_skipped'
}

/** No deck on the page and none being built: the reader can load it. */
export interface DeckAbsentViewState extends ReportViewStateBase {
  kind: 'deck_absent'
}

/**
 * The one view state of a report's deck surface. Components switch on
 * `kind` with an exhaustive `never` check instead of reading deck booleans.
 */
export type ReportViewState =
  | DeckReadyViewState
  | DeckFetchingViewState
  | DeckGeneratingViewState
  | DeckFailedViewState
  | DeckSkippedViewState
  | DeckAbsentViewState

export type ReportViewStateKind = ReportViewState['kind']

/**
 * Whether a section is an exhibit shown as a failed-slide placeholder: the
 * one predicate the pager and the banner scope (`reportFailureScope`) share.
 */
export function isFailedExhibitSection(section: ReportSectionState): boolean {
  return section.sectionId.kind === 'exhibit' && section.availability === 'failed'
}

/** Exhibits the accepted publication marks failed. */
export function failedExhibitsOf(
  availability: ReportAvailability | null | undefined,
): Array<FailedExhibit> {
  if (!availability) {
    return []
  }
  const failed: Array<FailedExhibit> = []
  for (const section of availability.sections) {
    if (section.sectionId.kind !== 'exhibit' || !isFailedExhibitSection(section)) {
      continue
    }
    failed.push({
      slideId: section.sectionId.slideId,
      failureReason: section.failureReason ?? null,
      attemptId: section.attemptId ?? null,
    })
  }
  return failed
}

/**
 * The report's view state from accepted section state and the deck on the
 * page. Precedence: a failed deck, a fetch in flight, a readable deck (kept
 * on screen while a new one generates), a study without a deck, a deck being
 * built, else nothing loaded yet.
 */
export function reportViewState(
  availability: ReportAvailability | null | undefined,
  deck: ReportDeckInput,
): ReportViewState {
  const failedExhibits = failedExhibitsOf(availability)
  const status = deck.status ?? availability?.deckStatus ?? null
  if (status === 'failed') {
    return { kind: 'deck_failed', failedExhibits }
  }
  if (deck.isFetching) {
    return { kind: 'deck_fetching', failedExhibits }
  }
  if (deck.hasSlides) {
    return { kind: 'deck_ready', isUpdating: status === 'processing', failedExhibits }
  }
  if (status === 'skipped') {
    return { kind: 'deck_skipped', failedExhibits }
  }
  if (status === 'processing') {
    return { kind: 'deck_generating', failedExhibits }
  }
  return { kind: 'deck_absent', failedExhibits }
}

/** The inputs the report page resolves its served deck status from. */
export interface ServedDeckStatusInput {
  /** The status the study row serves. */
  study: DeckStatus | null | undefined
  /** The readiness deck step (a failed report pipeline fails it). */
  deckStep: ReportReadinessStepStatus | null | undefined
  /** A live status from the socket, until the next reload replaces it. */
  live: DeckStatus | null
}

/**
 * The deck status a report page serves: a failure from the study or its
 * readiness wins, then a deck generating (served or announced live), else
 * what the study says.
 */
export function servedDeckStatus(input: ServedDeckStatusInput): DeckStatus | null {
  if (input.study === 'failed' || input.deckStep === 'failed') {
    return 'failed'
  }
  if (input.live === 'processing' || input.study === 'processing') {
    return 'processing'
  }
  return input.study ?? null
}

/** Compile-time exhaustiveness for switches over `ReportViewState`. */
export function assertNeverViewState(state: never): never {
  throw new Error(`Unhandled report view state: ${JSON.stringify(state)}`)
}

/**
 * Where a storyline's slide links stand against the deck on the page: `live`
 * (the deck is readable, links open their slide), `pending` (the deck is
 * still being fetched or built, links wait) or `none` (no deck will show:
 * failed, skipped or not loaded, so no link renders).
 */
export type StorylineDeckLinks = 'live' | 'pending' | 'none'

/** The report is still generating and has no storyline yet. */
export interface StorylineLoadingViewState {
  kind: 'storyline_loading'
}

/** A storyline parsed at the boundary, with where its slide links stand. */
export interface StorylineReadyViewState {
  kind: 'storyline_ready'
  storyline: PublicStoryline
  deckLinks: StorylineDeckLinks
}

/**
 * No storyline once generation is over (not produced, or a report from
 * before storylines): the analyst tab keeps today's empty state.
 */
export interface StorylineFailedViewState {
  kind: 'storyline_failed'
}

/** A storyline arrived in a shape the page cannot trust (logged once by the parser). */
export interface StorylineMalformedViewState {
  kind: 'storyline_malformed'
}

/**
 * The one view state of the analyst tab's storyline header. Components
 * switch on `kind` with an exhaustive `never` check.
 */
export type StorylineViewState =
  | StorylineLoadingViewState
  | StorylineReadyViewState
  | StorylineFailedViewState
  | StorylineMalformedViewState

/** Where slide links stand for each deck view state. */
function storylineDeckLinks(deck: ReportViewState): StorylineDeckLinks {
  switch (deck.kind) {
    case 'deck_ready':
      return 'live'
    case 'deck_fetching':
    case 'deck_generating':
      return 'pending'
    case 'deck_failed':
    case 'deck_skipped':
    case 'deck_absent':
      return 'none'
    default:
      return assertNeverViewState(deck)
  }
}

/**
 * The storyline header's view state from accepted section state, the deck's
 * view state and the storyline parsed at the boundary (`null` when the
 * analysis carries none). Precedence: a parsed storyline is ready, a
 * rejected one is malformed, none while the report still generates is
 * loading, else failed.
 */
export function storylineViewState(
  availability: ReportAvailability | null | undefined,
  deck: ReportViewState,
  parsed: BoundaryParseResult<PublicStoryline> | null,
): StorylineViewState {
  if (parsed?.ok) {
    return { kind: 'storyline_ready', storyline: parsed.data, deckLinks: storylineDeckLinks(deck) }
  }
  if (parsed && !parsed.ok) {
    return { kind: 'storyline_malformed' }
  }
  const generating =
    availability?.reportStatus === 'generating' ||
    availability?.generationLifecycle === 'allocated' ||
    availability?.generationLifecycle === 'running'
  return generating ? { kind: 'storyline_loading' } : { kind: 'storyline_failed' }
}

/** Compile-time exhaustiveness for switches over `StorylineViewState`. */
export function assertNeverStorylineState(state: never): never {
  throw new Error(`Unhandled storyline view state: ${JSON.stringify(state)}`)
}
